<template>
  <div
    ref="viewport"
    class="zoomable-image-viewport"
    :class="{ 'zoomable-image-viewport--panning': isPanning }"
    :style="viewportStyle"
    @wheel.prevent="onWheel"
    @mousedown="onPointerDown"
  >
    <!-- 이미지·이미지좌표 도형: zoom transform 적용 -->
    <div
      ref="layer"
      class="zoomable-image-layer"
      :class="{ 'zoomable-image-layer--pixelated': pixelated }"
      :style="layerStyle"
    >
      <slot />
    </div>

    <!-- 라벨/HUD 등 화면 고정 UI: scale 없음 -->
    <div class="zoomable-image-overlay">
      <slot
        name="overlay"
        :transform="transform"
        :image-to-screen="imageToScreenBound"
        :screen-to-image="screenToImageBound"
      />
    </div>
  </div>
</template>

<script>
import {
  createZoomEngine,
  THERMAL_ZOOM_LEVELS,
  imageToScreen,
  screenToImage,
  zoomCssVars
} from '@/lib/zoomable-viewer';

/**
 * 이산 줌 뷰포트 어댑터 (zoomable-viewer).
 * - default slot: 확대되는 이미지/콘텐츠
 * - overlay slot: 화면 크기 고정 도구 (라벨, HUD)
 */
export default {
  name: 'ZoomableImageViewport',
  props: {
    width: {
      type: Number,
      default: 640
    },
    height: {
      type: Number,
      default: 512
    },
    /** 허용 배율. 기본 IR/온도용 1,2,4,8,16 */
    levels: {
      type: Array,
      default: () => [...THERMAL_ZOOM_LEVELS]
    },
    /**
     * pan 시작을 막을 CSS selector (결함 박스 핸들 등)
     */
    panIgnoreSelector: {
      type: String,
      default: ''
    },
    /** 온도 격자 경계 유지용 nearest-neighbor 렌더 */
    pixelated: {
      type: Boolean,
      default: true
    },
    /** 이미지가 뷰포트 밖으로 빠져 빈 화면이 보이지 않게 pan 클램프 */
    clampBounds: {
      type: Boolean,
      default: true
    },
    /** 줌 대상 콘텐츠 크기(미지정 시 viewport와 동일) */
    contentWidth: {
      type: Number,
      default: null
    },
    contentHeight: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      engine: null,
      unsubscribe: null,
      transform: { x: 0, y: 0, k: 1, levelIndex: 0 },
      isPanning: false,
      panLast: null
    };
  },
  computed: {
    viewportStyle() {
      return {
        width: `${this.width}px`,
        height: `${this.height}px`,
        ...zoomCssVars(this.transform.k)
      };
    },
    layerStyle() {
      return {
        transform: `translate(${this.transform.x}px, ${this.transform.y}px) scale(${this.transform.k})`,
        transformOrigin: '0 0'
      };
    },
    resolvedContentWidth() {
      return this.contentWidth > 0 ? this.contentWidth : this.width;
    },
    resolvedContentHeight() {
      return this.contentHeight > 0 ? this.contentHeight : this.height;
    }
  },
  watch: {
    levels: {
      deep: true,
      handler() {
        this.recreateEngine();
      }
    },
    width() {
      this.syncEngineBounds();
    },
    height() {
      this.syncEngineBounds();
    },
    contentWidth() {
      this.syncEngineBounds();
    },
    contentHeight() {
      this.syncEngineBounds();
    },
    clampBounds() {
      this.syncEngineBounds();
    }
  },
  mounted() {
    this.recreateEngine();
    window.addEventListener('mousemove', this.onPointerMove);
    window.addEventListener('mouseup', this.onPointerUp);
  },
  beforeUnmount() {
    this.teardownEngine();
    window.removeEventListener('mousemove', this.onPointerMove);
    window.removeEventListener('mouseup', this.onPointerUp);
  },
  beforeDestroy() {
    this.teardownEngine();
    window.removeEventListener('mousemove', this.onPointerMove);
    window.removeEventListener('mouseup', this.onPointerUp);
  },
  methods: {
    imageToScreenBound(imageX, imageY) {
      return imageToScreen(imageX, imageY, this.transform);
    },
    screenToImageBound(screenX, screenY) {
      return screenToImage(screenX, screenY, this.transform);
    },
    recreateEngine() {
      this.teardownEngine();
      this.engine = createZoomEngine({
        levels: this.levels,
        viewportWidth: this.width,
        viewportHeight: this.height,
        contentWidth: this.resolvedContentWidth,
        contentHeight: this.resolvedContentHeight,
        clampBounds: this.clampBounds
      });
      this.transform = this.engine.getTransform();
      this.unsubscribe = this.engine.subscribe((next) => {
        this.transform = { ...next };
        this.$emit('transform', next);
      });
      this.$emit('transform', this.transform);
    },
    syncEngineBounds() {
      if (!this.engine) return;
      this.engine.setBounds({
        viewportWidth: this.width,
        viewportHeight: this.height,
        contentWidth: this.resolvedContentWidth,
        contentHeight: this.resolvedContentHeight,
        clampBounds: this.clampBounds
      });
    },
    teardownEngine() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
      this.engine = null;
    },
    getTransform() {
      return this.engine ? this.engine.getTransform() : this.transform;
    },
    reset() {
      if (this.engine) {
        this.engine.reset();
      }
    },
    zoomIn(anchorX, anchorY) {
      if (!this.engine) return;
      const { x, y } = this.resolveAnchor(anchorX, anchorY);
      this.engine.zoomInAt(x, y);
    },
    zoomOut(anchorX, anchorY) {
      if (!this.engine) return;
      const { x, y } = this.resolveAnchor(anchorX, anchorY);
      this.engine.zoomOutAt(x, y);
    },
    resolveAnchor(anchorX, anchorY) {
      if (Number.isFinite(anchorX) && Number.isFinite(anchorY)) {
        return { x: anchorX, y: anchorY };
      }
      return { x: this.width / 2, y: this.height / 2 };
    },
    localPointFromEvent(event) {
      const viewport = this.$refs.viewport;
      if (!viewport) {
        return { x: 0, y: 0 };
      }
      const rect = viewport.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    },
    onWheel(event) {
      if (!this.engine) return;
      const { x, y } = this.localPointFromEvent(event);
      this.engine.handleWheel(event.deltaY, x, y);
    },
    shouldIgnorePan(event) {
      if (!this.panIgnoreSelector) {
        return false;
      }
      const target = event.target;
      return !!(
        target &&
        typeof target.closest === 'function' &&
        target.closest(this.panIgnoreSelector)
      );
    },
    onPointerDown(event) {
      if (event.button !== 0) return;
      if (this.shouldIgnorePan(event)) return;
      this.isPanning = true;
      this.panLast = { x: event.clientX, y: event.clientY };
    },
    onPointerMove(event) {
      if (!this.isPanning || !this.engine || !this.panLast) return;
      const dx = event.clientX - this.panLast.x;
      const dy = event.clientY - this.panLast.y;
      this.panLast = { x: event.clientX, y: event.clientY };
      this.engine.panBy(dx, dy);
    },
    onPointerUp() {
      this.isPanning = false;
      this.panLast = null;
    }
  }
};
</script>

<style scoped>
.zoomable-image-viewport {
  max-width: 100%;
  overflow: hidden;
  position: relative;
  touch-action: none;
  cursor: grab;
  user-select: none;
}

.zoomable-image-viewport--panning {
  cursor: grabbing;
}

.zoomable-image-layer {
  width: 100%;
  height: 100%;
  will-change: transform;
}

.zoomable-image-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  overflow: hidden;
}

.zoomable-image-layer--pixelated ::v-deep img,
.zoomable-image-layer--pixelated ::v-deep .v-image__image {
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  image-rendering: pixelated;
}
</style>
